import { useCallback, useEffect, useRef, useState } from 'react';

type Options = {
  onCountDownEnd: () => void;
};

export const useTimer = (baseDuration: number, options?: Options) => {
  const { onCountDownEnd } = options || {};

  const [isFirstTime, setIsFirstTime] = useState(true);
  const [countDown, setCountDown] = useState(baseDuration);
  const [intervalKey, setIntervalKey] = useState<NodeJS.Timer | undefined>();
  const [timerDuration, setTimerDuration] = useState(baseDuration);

  const countDownRef = useRef({ countDown, intervalKey });
  countDownRef.current = { countDown, intervalKey };

  const startCountDown = useCallback(
    (args?: { nextIsFirstTime?: boolean }) => {
      const { nextIsFirstTime = false } = args || {};

      if (isFirstTime !== nextIsFirstTime) setIsFirstTime(nextIsFirstTime);

      setCountDown(timerDuration);

      // Clean old interval
      intervalKey && clearInterval(intervalKey);

      const newIntervalKey = setInterval(() => {
        if (countDownRef?.current?.countDown === 0) {
          onCountDownEnd?.();
          clearInterval(newIntervalKey);
          setIntervalKey(undefined);
          return;
        }

        setCountDown((prevState) => prevState - 1);
      }, 1000);

      setIntervalKey(newIntervalKey);
    },
    [timerDuration, intervalKey, countDown, onCountDownEnd]
  );

  useEffect(() => {
    startCountDown({ nextIsFirstTime: true });

    return () => {
      countDownRef.current?.intervalKey && clearInterval(countDownRef.current?.intervalKey);
    };
  }, []);

  const increaseTimer = useCallback(() => {
    setTimerDuration(60);
  }, [timerDuration]);

  const resetTimer = useCallback(() => {
    setTimerDuration(baseDuration);
  }, []);

  const stopCountDownTimer = useCallback(() => {
    countDownRef.current?.intervalKey && clearInterval(countDownRef.current?.intervalKey);
  }, [countDownRef.current?.intervalKey]);

  return {
    startCountDown,
    increaseTimer,
    resetTimer,
    isTimerActive: intervalKey,
    countDown,
    isFirstTime,
    stopCountDownTimer,
  };
};

export default useTimer;
